/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable quotes */
import { Box, Divider, LinearProgress, Tab, Tabs, Typography } from '@mui/material';
import { LayoutBase } from '../../layouts';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AnomalyService, IAnomalias, IDetalhesAnomalia, IImagemAnomalia, FormErrors, IAnomaliasPut } from '../../services/api/anomaly/AnomalyService';
import { useNavigate, useParams } from 'react-router-dom';
import { ToolsDetails } from '../../components/custom';
import HistoricoAnomalia from '../../components/custom/Anomaly/HistoricoAnomalia';
import { CustomAlert } from '../../components/shared';
import { formatDateForInputDate, helpers } from '../../services/helpers/helpers';
import FormEditAnomaly, { ImgData } from '../../components/custom/Anomaly/FormEditAnomaly';
import { useAuthContext } from '../../context';

export default function EditAnomaly() {
    const url = useParams();
    const [value, setValue] = useState('1');
    const navigate = useNavigate();
    const [alertInfo, setAlertInfo] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' | 'info', key: string }>({
        open: false,
        message: '',
        severity: 'success',
        key: '',
    });

    const [errors, setErrors] = useState<FormErrors>({});

    const { userData } = useAuthContext();
    const pagePermissionsString = useMemo(() => {
        return userData?.nivel_acesso?.acompanhamentos?.anomalias;
    }, [userData]);

    const hasActionPermission = useCallback((action: 'add' | 'edit' | 'remove' | 'view'): boolean => {
        if (typeof pagePermissionsString === 'string' && pagePermissionsString !== '') {
            const actions = pagePermissionsString.split(',').map(s => s.trim());
            return actions.includes(action);
        }
        return false;
    }, [pagePermissionsString]);

    const [data, setData] = useState<IAnomalias>({
        acao: '',
        area_responsavel: '',
        descricao_atividades: '',
        id_barragem: url.id ?? '',
        matricula_responsavel_procedimento: '',
        nome_responsavel_procedimento: '',
        prazo_previsto_fim: '',
        prazo_previsto_inicial: '',
        prazo_real_fim: '',
        prazo_real_inicial: '',
        relatorio_inspecao: '',
        uo_responsavel_procedimento: '',
        file: undefined,
        origem: '',
        estrutura: '',
        codigo_anomalia: '',
        detalhes: [],
        imagens: [],
    });

    const [currentDetalhe, setCurrentDetalhe] = useState<IDetalhesAnomalia>({
        data_inspecao: formatDateForInputDate(new Date().toISOString().split('T')[0]) || '',
        item: '',
        situacao: '',
        magnitude: '',
        npa: '',
        observacoes_gerais: '',
        vertimento_extravasor: false,
        fluxo: false,
        reservatorio_seco: false,
    });

    const [imageLegenda, setImageLegenda] = useState<string>('');
    const [imageDate, setImageDate] = useState<string>(formatDateForInputDate(new Date().toISOString().split('T')[0]) || '');
    const [imagens, setImagens] = useState<ImgData[] | undefined>(undefined);

    const [loading, setLoading] = useState<boolean>(false);

    const handleChange = <K extends keyof IAnomalias>(key: K, value: IAnomalias[K]) => {
        setData(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const handleChangeDetalhe = <K extends keyof IDetalhesAnomalia>(key: K, value: IDetalhesAnomalia[K]) => {
        setCurrentDetalhe((prev: IDetalhesAnomalia) => {
            const newState = { ...prev, [key]: value };
            if (key === 'data_inspecao') {
                setImageDate(value as string);
            }
            return newState;
        });
    };

    const handleChangeFile = async (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.currentTarget.files?.[0] || null;

        if (file !== null && imagens) {
            const newImg = await helpers.fileToBase64WithValidation(file);
            const newDate = new Date().toISOString().split('T')[0];

            const updatedImagens = imagens.map((img, i) => {
                if (i === index) {
                    return {
                        ...img,
                        data: newImg,
                        nome: file.name,
                        date: newDate
                    };
                }
                return img;
            });

            setImagens(updatedImagens);
        }
    };

    const handleChangeLegenda = (index: number, newLegenda: string) => {
        if (imagens) {
            const updatedImagens = imagens.map((img, i) => {
                if (i === index) {
                    return {
                        ...img,
                        legenda: newLegenda
                    };
                }
                return img;
            });
            
            setImagens(updatedImagens);
        }
    };

    const handleImageDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setImageDate(e.target.value);
    };

    const validateForm = (): boolean => {
        const newErrors: Partial<{ [K in keyof IAnomalias]?: boolean }> = {};
        const newDetalhesErrors: Partial<{ [D in keyof IDetalhesAnomalia]?: boolean }> = {};

        if (data.estrutura.trim() === '') {
            newErrors.estrutura = true;
        }
        if (currentDetalhe.data_inspecao.trim() === '') {
            newDetalhesErrors.data_inspecao = true;
        }
        if (currentDetalhe.item.trim() === '') {
            newDetalhesErrors.item = true;
        }
        if (currentDetalhe.situacao.trim() === '') {
            newDetalhesErrors.situacao = true;
        }
        if (currentDetalhe.magnitude.trim() === '') {
            newDetalhesErrors.magnitude = true;
        }
        if (currentDetalhe.npa.trim() === '') {
            newDetalhesErrors.npa = true;
        }

        setErrors({ ...newErrors, detalhes: newDetalhesErrors });

        const isValid = Object.keys(newErrors).length === 0 && Object.keys(newDetalhesErrors).length === 0;

        return isValid;
    };

    async function handleSave() {
        const isEditing = url.anomaly !== 'new';
        const hasAddPermission = hasActionPermission('add');
        const hasEditPermission = hasActionPermission('edit');
        if ((!isEditing && !hasAddPermission) || (isEditing && !hasEditPermission)) {
            setAlertInfo({ open: true, message: 'Você não tem permissão para realizar esta ação.', severity: 'error', key: new Date().toISOString() });
            return;
        }
        if (validateForm()) {
            setLoading(true);

            try {
                await sendAnomalyPayload(imagens);

            } catch (error: any) {
                setAlertInfo({ open: true, message: 'Erro inesperado ao salvar: ' + error.message, severity: 'error', key: new Date().toISOString() });
                setLoading(false);
            }
        } else {
            setAlertInfo({ open: true, message: 'Por favor, preencha todos os campos obrigatórios.', severity: 'warning', key: new Date().toISOString() });
        }
    }

    const sendAnomalyPayload = async (images: IImagemAnomalia[] | null | undefined) => {
        const processedDetalhe: Partial<IDetalhesAnomalia> = { ...currentDetalhe };

        if (typeof processedDetalhe.fluxo === 'boolean') {
            (processedDetalhe.fluxo as any) = processedDetalhe.fluxo.toString();
        }
        if (typeof processedDetalhe.vertimento_extravasor === 'boolean') {
            (processedDetalhe.vertimento_extravasor as any) = processedDetalhe.vertimento_extravasor.toString();
        }
        if (typeof processedDetalhe.reservatorio_seco === 'boolean') {
            (processedDetalhe.reservatorio_seco as any) = processedDetalhe.reservatorio_seco.toString();
        }

        let filteredImages = undefined;

        if(images){
            filteredImages = images.filter(img => !img.data.includes('https://s3.htisystem.com.br'));
        }

        const anomalyObject: Omit<IAnomaliasPut, 'file' | 'historico' | 'uuid' | 'data_armazenamento' | 'fluxo'> = {
            estrutura: data.estrutura,
            id_barragem: data.id_barragem,
            detalhes: processedDetalhe as IDetalhesAnomalia,
            imagens: filteredImages,
            acao: data.acao,
            area_responsavel: data.area_responsavel,
            descricao_atividades: data.descricao_atividades,
            matricula_responsavel_procedimento: data.matricula_responsavel_procedimento,
            nome_responsavel_procedimento: data.nome_responsavel_procedimento,
            prazo_previsto_fim: data.prazo_previsto_fim,
            prazo_previsto_inicial: data.prazo_previsto_inicial,
            prazo_real_fim: data.prazo_real_fim,
            prazo_real_inicial: data.prazo_real_inicial,
            relatorio_inspecao: data.relatorio_inspecao,
            uo_responsavel_procedimento: data.uo_responsavel_procedimento,
            origem: data.origem,
            codigo_anomalia: data.codigo_anomalia,
        };

        await AnomalyService.update(anomalyObject as IAnomaliasPut, url.anomaly ?? '')
            .then((response) => {
                if (response instanceof Error) {
                    setAlertInfo({ open: true, message: 'Erro ao atualizar! ' + response.message, severity: 'error', key: new Date().toISOString() });
                } else {
                    setAlertInfo({ open: true, message: 'Anomalia Atualizada! ', severity: 'success', key: new Date().toISOString() });
                    getAnomalyData();
                    setTimeout(() => {
                        navigate(-1);
                    }, 1000);
                }
            })
            .catch(apiError => {
                setAlertInfo({ open: true, message: 'Erro de rede ou API ao atualizar!', severity: 'error', key: new Date().toISOString() });
            });

        setLoading(false);
    };

    async function getAnomalyData() {
        if (url.anomaly && url.anomaly !== 'new') {
            setLoading(true);
            try {
                const response = await AnomalyService.get(url.anomaly);
                const receivedAnomaly = Array.isArray(response) ? response[0] : response;

                const newData: IAnomalias = {
                    ...receivedAnomaly,
                    prazo_previsto_fim: formatDateForInputDate(receivedAnomaly.prazo_previsto_fim) || '',
                    prazo_previsto_inicial: formatDateForInputDate(receivedAnomaly.prazo_previsto_inicial) || '',
                    prazo_real_fim: formatDateForInputDate(receivedAnomaly.prazo_real_fim) || '',
                    prazo_real_inicial: formatDateForInputDate(receivedAnomaly.prazo_real_inicial) || '',
                    file: undefined,
                    detalhes: receivedAnomaly.detalhes ? (Array.isArray(receivedAnomaly.detalhes) ? receivedAnomaly.detalhes : [receivedAnomaly.detalhes]) : [],
                    imagens: receivedAnomaly.imagens || [],
                };


                const imagens_recebidas = receivedAnomaly.imagens;
                let imagens_finais = undefined;

                if (imagens_recebidas === null || imagens_recebidas.length === 0) {
                    imagens_finais = undefined;
                } else {
                    imagens_finais = imagens_recebidas.slice(-3);
                }
                setImagens(imagens_finais);
                setData(newData);

                if (newData.detalhes && newData.detalhes.length > 0) {
                    const currentDetailFromApi = newData.detalhes[0];
                    setCurrentDetalhe(_prevDetalhe => ({
                        data_inspecao: formatDateForInputDate(currentDetailFromApi.data_inspecao) || '',
                        item: currentDetailFromApi.item || '',
                        observacoes_gerais: currentDetailFromApi.observacoes_gerais || '',
                        vertimento_extravasor: currentDetailFromApi.vertimento_extravasor ?? false,
                        fluxo: (currentDetailFromApi as any).fluxo === 'true',
                        reservatorio_seco: currentDetailFromApi.reservatorio_seco ?? false,
                        situacao: currentDetailFromApi.situacao || '',
                        magnitude: currentDetailFromApi.magnitude || '',
                        npa: currentDetailFromApi.npa || '',
                        historico: currentDetailFromApi.historico || [],
                        codigo_anomalia: currentDetailFromApi.codigo_anomalia || '',
                        origem: currentDetailFromApi.origem || '',
                    }));
                    setImageDate(formatDateForInputDate(currentDetailFromApi.data_inspecao) || '');
                } else {
                    setCurrentDetalhe({
                        data_inspecao: formatDateForInputDate(new Date().toISOString().split('T')[0]) || '',
                        item: '', situacao: '', magnitude: '', npa: '', observacoes_gerais: '',
                        vertimento_extravasor: false, fluxo: false, reservatorio_seco: false,
                    });
                    setImageDate(formatDateForInputDate(new Date().toISOString().split('T')[0]) || '');
                }
            } catch (error) {
                setAlertInfo({ open: true, message: 'Erro inesperado ao carregar anomalia.', severity: 'error', key: new Date().toISOString() });
            } finally {
                setLoading(false);
            }
        }
    }

    useEffect(() => {
        getAnomalyData();
    }, [url.anomaly]);

    useEffect(() => {
        if (currentDetalhe.data_inspecao && imageDate !== currentDetalhe.data_inspecao) {
            setImageDate(currentDetalhe.data_inspecao);
        }
    }, [currentDetalhe.data_inspecao]);


    const handleChangeTab = (event: React.SyntheticEvent, newValue: string) => {
        setValue(newValue);
    };

    return (
        <LayoutBase
            title={url.anomaly === 'new' ? 'Cadastro de Anomalia' : 'Edição de Anomalia'}
            tollBar={
                <ToolsDetails
                    onClickSave={handleSave}
                    showDeletButton={false}
                    showNewButton={false}
                    showSaveButton={url.anomaly === 'new' ? hasActionPermission('add') : hasActionPermission('edit')}
                />
            }
        >
            <Box padding={2}>
                <Box sx={{ width: '100%', justifyItems: 'center' }}>
                    <Tabs
                        value={value}
                        onChange={handleChangeTab}
                    >
                        <Tab value={'1'} label='Formulário' />
                        <Tab value={'2'} label='Histórico' disabled={url.anomaly === 'new' ? true : false} />
                    </Tabs>
                </Box>
                {value === '1' && (
                    <Box>
                        <Typography variant='h5'>{url.anomaly === 'new' ? 'Cadastro de Anomalia' : 'Edição de Anomalia'}</Typography>
                        <Divider />
                        {loading ?

                            <LinearProgress />
                            :
                            <FormEditAnomaly
                                data={data}
                                currentDetalhe={currentDetalhe}
                                erros={errors}
                                handleChange={handleChange}
                                handleChangeDetalhe={handleChangeDetalhe}

                                handleChangeFile={handleChangeFile}
                                handleChangeLegenda={handleChangeLegenda}
                                
                                imageLegenda={imageLegenda}
                                imagens={imagens}
                                imageDate={imageDate}
                                handleImageDateChange={handleImageDateChange}
                                hasEditPermission={hasActionPermission('edit')}
                                hasAddPermission={hasActionPermission('add')}
                            />
                        }
                    </Box>
                )}
                {value === '2' && (
                    <Box>
                        <Typography variant='h5'>Histórico </Typography>
                        <Divider />
                        <HistoricoAnomalia data={data} />
                    </Box>
                )}
            </Box>
            {alertInfo.open &&
                <CustomAlert
                    message={alertInfo.message}
                    severity={alertInfo.severity}
                    key={alertInfo.key}
                />
            }
        </LayoutBase >
    );
}